import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PaywallModal from "@/components/PaywallModal";
import Community from "@/pages/Community";
import MyWorlds from "@/pages/MyWorlds";
import AdBanner from "@/components/AdBanner";
import { toast } from "sonner";
import { X } from "lucide-react";

const MD_QUERY = "(min-width: 768px)";

function useGridCols() {
  const [cols, setCols] = useState(() => (window.matchMedia(MD_QUERY).matches ? 3 : 1));
  useEffect(() => {
    const mq = window.matchMedia(MD_QUERY);
    const onChange = () => setCols(mq.matches ? 3 : 1);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return cols;
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("flagship");
  const [worlds, setWorlds] = useState([]);
  const [adventures, setAdventures] = useState([]);
  const [selectedWorld, setSelectedWorld] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [hardMode, setHardMode] = useState(false);
  const [paywall, setPaywall] = useState(null);
  const [loading, setLoading] = useState(false);
  const gridCols = useGridCols();

  useEffect(() => {
    (async () => {
      const [w, a] = await Promise.all([api.get("/worlds"), api.get("/adventures")]);
      setWorlds(w.data);
      setAdventures(a.data);
    })();
  }, []);

  useEffect(() => {
    if (searchParams.get("upgrade") === "1" && !user?.premium) {
      setPaywall({ reason: "world" });
      searchParams.delete("upgrade");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, user?.premium]);

  const beginQuest = async () => {
    if (!selectedWorld || !selectedVersion) return;
    if (!user.premium && hardMode) {
      setPaywall({ reason: "hard_mode" });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/adventures/start", {
        world_id: selectedWorld.id,
        version_id: selectedVersion.id,
        hard_mode: hardMode,
      });
      navigate(`/play/${data.adventure.adventure_id}`);
    } catch (e) {
      if (e.response?.status === 402) {
        setPaywall({ reason: "limit", message: e.response.data.detail });
      } else {
        toast.error(e.response?.data?.detail || "Failed to start");
      }
    } finally {
      setLoading(false);
    }
  };

  const openAdventure = (id) => navigate(`/play/${id}`);

  const removeAdventure = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this chronicle?")) return;
    await api.delete(`/adventures/${id}`);
    setAdventures(adventures.filter((a) => a.adventure_id !== id));
  };

  const mockUpgrade = async () => {
    setPaywall(null);
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open portal");
    }
  };

  if (!user) return null;

  const selectedIndex = worlds.findIndex((w) => w.id === selectedWorld?.id);
  const configSlot = selectedIndex < 0 ? -1 : Math.min(worlds.length - 1, Math.floor(selectedIndex / gridCols) * gridCols + gridCols - 1);

  const selectWorld = (w) => {
    if (selectedWorld?.id === w.id) {
      setSelectedWorld(null);
      return;
    }
    setSelectedWorld(w);
    setSelectedVersion(w.versions[0]);
  };

  const TABS = [
    { key: "flagship", label: "Flagship" },
    { key: "community", label: "Community" },
    { key: "myworlds", label: "My Worlds" },
  ];

  return (
    <div className="min-h-screen relative z-10" data-testid="dashboard">
      {/* Header */}
      <header className="border-b border-amber-500/20 backdrop-blur-md bg-stone-950/60 sticky top-0 z-20 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-serif-display gold text-base sm:text-lg tracking-widest truncate">MYTHICMATRIX</div>
            <div className="font-mono text-[0.6rem] uppercase tracking-widest text-stone-500">The Vault</div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <div className="font-mono text-xs text-stone-400 hidden sm:block">
              <UserBadge user={user} /> · {user.name}
            </div>
            {user.premium && (
              <button data-testid="manage-subscription-btn" onClick={openPortal} className="btn-ghost">Manage</button>
            )}
            <button data-testid="logout-btn" onClick={logout} className="btn-ghost">Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-8 sm:space-y-10">
        <nav className="flex gap-1 border-b border-amber-500/20 overflow-x-auto no-scrollbar" data-testid="dashboard-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`shrink-0 px-3 sm:px-4 py-3 min-h-[44px] font-serif-display text-xs tracking-[0.25em] sm:tracking-[0.3em] uppercase transition-colors border-b-2 -mb-px ${
                tab === t.key ? "border-amber-500 gold" : "border-transparent text-stone-500 hover:text-stone-300"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {tab === "community" && <Community />}
        {tab === "myworlds" && <MyWorlds />}

        {tab === "flagship" && <>
        <AdBanner slot="dashboard-top" format="horizontal" />
        {/* Continue Section */}
        {adventures.length > 0 && (
          <section data-testid="continue-section">
            <h2 className="font-serif-display text-xs tracking-[0.4em] gold uppercase mb-4">Your Chronicles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {adventures.map((a) => (
                <div
                  key={a.adventure_id}
                  data-testid={`adventure-${a.adventure_id}`}
                  onClick={() => openAdventure(a.adventure_id)}
                  className="tome-card rounded-sm p-4 cursor-pointer group"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="font-serif-body text-lg leading-tight">{a.title}</div>
                    <button onClick={(e) => removeAdventure(a.adventure_id, e)} data-testid={`delete-adventure-${a.adventure_id}`} aria-label="Delete chronicle" className="text-stone-600 hover:text-red-400 w-10 h-10 -mr-2 -mt-2 flex items-center justify-center shrink-0"><X size={16} /></button>
                  </div>
                  <div className="flex flex-wrap gap-2 font-mono text-[0.6rem] uppercase tracking-widest">
                    <span className="px-2 py-0.5 border border-amber-500/30 gold rounded-sm">Turn {a.turn_count}</span>
                    {!user.premium && a.turn_count >= 10 && (
                      <span
                        data-testid={`preview-paused-${a.adventure_id}`}
                        className="px-2 py-0.5 border border-emerald-500/50 text-emerald-300 rounded-sm"
                        title="Bookmarked — ascend to resume"
                      >
                        ⤷ Paused · Bookmarked
                      </span>
                    )}
                    {a.hard_mode && <span className="px-2 py-0.5 border border-red-500/40 text-red-400 rounded-sm">Hard</span>}
                    {a.is_locked && <span className="px-2 py-0.5 border border-purple-500/40 text-purple-300 rounded-sm">Locked</span>}
                    {a.is_dead && <span className="px-2 py-0.5 bg-red-950 text-red-300 rounded-sm">Fallen</span>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Worlds */}
        <section>
          <div className="mb-6">
            <div className="font-mono text-[0.6rem] uppercase tracking-[0.4em] gold mb-2">Volumes of the Archive</div>
            <h1 className="font-serif-display text-3xl sm:text-4xl font-black tracking-tight">Choose Your World</h1>
            <p className="font-prose text-stone-400 max-w-2xl mt-2">
              Each world is handcrafted with unique rules, tone, and mechanics. Commit carefully.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            {worlds.map((w, i) => (
              <React.Fragment key={w.id}>
                <WorldCard
                  world={w}
                  premium={user.premium}
                  selected={selectedWorld?.id === w.id}
                  onSelect={() => selectWorld(w)}
                />
                {selectedWorld && i === configSlot && (
                  <QuestConfig
                    world={selectedWorld}
                    version={selectedVersion}
                    onVersion={setSelectedVersion}
                    hardMode={hardMode}
                    onHardMode={(checked) => {
                      if (!user.premium && checked) {
                        setPaywall({ reason: "hard_mode" });
                        return;
                      }
                      setHardMode(checked);
                    }}
                    loading={loading}
                    onBegin={beginQuest}
                    onClose={() => setSelectedWorld(null)}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </section>

        </>}

        <footer className="pt-10 mt-4 border-t border-amber-500/10 flex items-center justify-between flex-wrap gap-2 font-mono text-[0.6rem] uppercase tracking-[0.3em] text-stone-500">
          <a href="/founders" data-testid="dashboard-founders-link" className="hover:gold transition-colors inline-block py-3">✦ The Founders' Wall</a>
          <span>Mythicmatrix · MMXXVI</span>
        </footer>
      </main>

      {paywall && (
        <PaywallModal
          reason={paywall.reason}
          message={paywall.message}
          onClose={() => setPaywall(null)}
        />
      )}
    </div>
  );
}

function UserBadge({ user }) {
  if (user.founder) {
    return (
      <span className="gold" data-testid="founder-badge" title="Founding Sovereign">
        ✦ Founding Sovereign
      </span>
    );
  }
  if (user.premium) {
    return (
      <span className="gold" data-testid="premium-badge">
        ◆ Sovereign{user.subscription_tier ? ` · ${user.subscription_tier}` : ""}
      </span>
    );
  }
  return <span data-testid="free-badge">Wayfarer</span>;
}

function QuestConfig({ world, version, onVersion, hardMode, onHardMode, loading, onBegin, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 80);
    return () => clearTimeout(t);
  }, [world.id]);

  return (
    <section ref={ref} className="md:col-span-3 tome-card rounded-sm p-5 sm:p-6 space-y-5 sm:space-y-6 fade-up border-amber-500/50 scroll-mt-24" data-testid="quest-config">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[0.6rem] uppercase tracking-[0.4em] gold mb-2">Chosen Volume</div>
          <div className="font-serif-display text-xl sm:text-2xl">{world.title}</div>
          <div className="font-serif-body italic text-stone-400 mt-1">{world.tagline}</div>
        </div>
        <button data-testid="quest-config-close" onClick={onClose} aria-label="Close" className="shrink-0 w-11 h-11 -mr-2 -mt-2 flex items-center justify-center text-stone-500 hover:text-amber-400">
          <X size={20} />
        </button>
      </div>

      <div>
        <div className="font-mono text-[0.6rem] uppercase tracking-widest text-stone-500 mb-2">Storyline Version</div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
          {world.versions.map((v) => (
            <button
              key={v.id}
              data-testid={`version-${v.id}`}
              onClick={() => onVersion(v)}
              className={`text-left px-4 py-3 min-h-[48px] rounded-sm border transition-colors ${
                version?.id === v.id ? "border-amber-500 bg-amber-500/10" : "border-stone-700 hover:border-amber-500/50"
              }`}
            >
              <div className="font-serif-body text-base leading-tight">{v.title}</div>
              <div className="font-mono text-[0.65rem] text-stone-500 mt-1">{v.hook}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 sticky bottom-0 -mx-5 sm:-mx-6 px-5 sm:px-6 pt-3 pb-3 bg-stone-950/95 backdrop-blur-md border-t border-amber-500/25 md:static md:mx-0 md:px-0 md:pt-0 md:pb-0 md:bg-transparent md:backdrop-blur-none md:border-0" data-testid="quest-config-actions">
        <label className="flex items-center gap-3 cursor-pointer group min-h-[44px]">
          <input
            data-testid="hard-mode-toggle"
            type="checkbox"
            checked={hardMode}
            onChange={(e) => onHardMode(e.target.checked)}
            className="w-5 h-5 accent-red-600 shrink-0"
          />
          <div>
            <div className="font-serif-body text-base">Hard Mode <span className="font-mono text-[0.6rem] uppercase text-red-400 ml-1">Sovereign Only</span></div>
            <div className="font-mono text-[0.65rem] text-stone-500">Limited undos · Random encounters · Permadeath</div>
          </div>
        </label>
        <button data-testid="begin-quest-btn" onClick={onBegin} disabled={loading} className="btn-gold w-full sm:w-auto min-h-[48px]">
          {loading ? "Opening..." : "Begin Quest"}
        </button>
      </div>
    </section>
  );
}

function WorldCard({ world, premium, selected, onSelect }) {
  const preview = !premium;
  return (
    <button
      data-testid={`world-card-${world.id}`}
      onClick={onSelect}
      aria-expanded={selected}
      className={`tome-card rounded-sm p-5 text-left w-full h-full flex flex-col active:scale-[0.99] transition-transform ${selected ? "ring-1 ring-amber-500" : ""}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="font-mono text-[0.6rem] uppercase tracking-widest gold">{world.mechanics_style.replace(/_/g, " ")}</div>
        {preview ? (
          <span className="font-mono text-[0.6rem] uppercase tracking-widest px-2 py-0.5 border border-emerald-600/40 text-emerald-400 rounded-sm">10-Turn Preview</span>
        ) : null}
      </div>
      <div className="font-serif-display text-xl leading-tight mb-1">{world.title}</div>
      <div className="font-serif-body italic text-stone-400 text-sm mb-3">{world.tagline}</div>
      <div className="font-prose text-stone-300 text-sm leading-relaxed line-clamp-3 sm:line-clamp-4 flex-1">{world.lore}</div>
      <div className="mt-4 pt-3 border-t border-amber-500/10 flex justify-between gap-3 font-mono text-[0.6rem] uppercase tracking-widest text-stone-500">
        <span>{world.genre}</span>
        {world.death_enabled && <span className="text-red-400 shrink-0">Death enabled</span>}
      </div>
      <div className={`mt-3 font-mono text-[0.6rem] uppercase tracking-[0.3em] ${selected ? "gold" : "text-stone-400"}`}>
        {selected ? "▾ Chosen · configure below" : "Tap to choose ▸"}
      </div>
    </button>
  );
}
